/**
 * Room message prices on the frontend (credit metering D12, D13, D15). Pure and hook-free so
 * it can be imported from lib code. Prices come from `@digital-twins/shared-types`.
 */
import {
  CHAT_CREDITS_PER_MSG,
  CREDIT_PRICING_POLICY_VERSION,
  ROOM_CREDITS_PER_MSG,
  conversationMessageCost,
  resolveCreditPricingVersion,
  roomMessageCredits,
} from '@digital-twins/shared-types'

// ── Room prices ───────────────────────────────────────────────────────────────

export type RoomPriceKind = 'chat' | 'focus_group'

/** The multiplier a message that adds a new video pays, read off the price list. */
export function newVideoMultiplier(): number {
  return resolveCreditPricingVersion(CREDIT_PRICING_POLICY_VERSION).rooms.newVideoMultiplier
}

/** Today's base credits per message for a kind and tier, or undefined for an unknown tier. */
export function todayRoomBase(kind: RoomPriceKind, tier: string | null | undefined): number | undefined {
  if (!tier) {
    return undefined
  }
  return (kind === 'chat' ? CHAT_CREDITS_PER_MSG : ROOM_CREDITS_PER_MSG)[tier]
}

export interface RoomBaseInput {
  kind: RoomPriceKind
  tier?: string | null
  /** The audience payload's effective rate (today's price or a custom rate). */
  quotedBase?: number | null
  enrolled: boolean
}

/**
 * Base credits per message before the level multiplier.
 *
 * Not enrolled: the audience's quoted rate, else today's tier table (exactly today).
 * Enrolled: the audience payload cannot say whether its rate is a custom one, so a quoted rate
 * that differs from today's tier price is read as a custom rate (it wins, D13); a rate equal to
 * today's price is the default and the new list applies. The server price is authoritative on a
 * room that exists (`ChatRoomDto.creditsPerMessage`); this is the pre-create quote.
 */
export function roomBaseRate(input: RoomBaseInput): number | null {
  const { kind, tier, quotedBase, enrolled } = input
  const quoted = typeof quotedBase === 'number' && Number.isFinite(quotedBase) ? quotedBase : null
  const today = todayRoomBase(kind, tier)
  if (!enrolled) {
    return quoted ?? today ?? null
  }
  if (!tier || today === undefined) {
    return quoted
  }
  const customRate = quoted !== null && quoted !== today ? quoted : null
  try {
    return roomMessageCredits({
      kind,
      tier,
      level: 'low',
      levelMultiplier: 1,
      carriesNewVideo: false,
      enrolled: true,
      baseOverride: customRate,
    })
  } catch {
    return quoted
  }
}

export interface RoomQuoteInput {
  baseRate: number | null | undefined
  multiplier: number
  enrolled: boolean
  carriesNewVideo?: boolean
}

/** Credits for one message: base x level multiplier, doubled by a new video for an enrolled account. */
export function roomQuote(input: RoomQuoteInput): number | null {
  const { baseRate, multiplier, enrolled, carriesNewVideo = false } = input
  if (typeof baseRate !== 'number') {
    return null
  }
  const credits = conversationMessageCost(baseRate, multiplier)
  return enrolled && carriesNewVideo ? credits * newVideoMultiplier() : credits
}

/**
 * Whether any attached video is new to the room: a hosted url that no earlier message in the
 * room carried. Approximate by design (the server is authoritative and charges by video id).
 */
export function carriesNewRoomVideo(
  attachedVideoUrls: ReadonlyArray<string>,
  watchedVideoUrls: ReadonlySet<string> | ReadonlyArray<string>,
): boolean {
  const watched = watchedVideoUrls instanceof Set ? watchedVideoUrls : new Set(watchedVideoUrls)
  return attachedVideoUrls.some((url) => url.length > 0 && !watched.has(url))
}

/** Hosted video urls already sent in a room's message history. */
export function watchedVideoUrlsOf(
  messages: ReadonlyArray<{ videoRefs?: ReadonlyArray<{ url: string }> | null }>,
): Set<string> {
  const urls = new Set<string>()
  for (const message of messages) {
    for (const video of message.videoRefs ?? []) {
      urls.add(video.url)
    }
  }
  return urls
}
